import {
  ModelInput, ModelSelectSearchInput, EmptyInput,
  Collapsible,
  ModelNewNumericInput,
  RadioYesNoInput,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import { isNonEmptyNumber } from '@equifinance/shared/utils';
import { useLenderOptions } from '../../../../models/lendersModel';
import {
  ConsolidationType,
  CreditItem,
  NonRevolvingAccountTypes,
  useCreditContext,
} from '../../../../contexts/credit';
import {
  CreditDetailsContainer,
  CreditDetailsRow,
  DetailsTitle,
  EquifaxAccordionHeaderShadow,
} from '../CreditDetails';
import EquifaxDetails from '../EquifaxDetails/EquifaxDetails';

const collapsibleWrapperCss = css`
  padding: 0;
`.toString();

const RowBreakLine = styled.div`${({ theme }) => `
  margin: 0 ${theme.padding[9]};
  height: ${theme.borderWidth[2]};
  background-color: ${theme.colours.neutral[8]};
`}`;

export default function UnsecuredCreditPackagingDetails({ creditItem }: { creditItem: CreditItem }) {
  const { credit } = useCreditContext();

  const lenders = useLenderOptions('unsecured');

  const isShowForm = !creditItem.ignored;
  const isShowLender = isShowForm && creditItem.consolidationType != null;
  const isShowAffordability = isShowLender && !creditItem.isUnderIVA;
  const isShowRemainingTermManual = !creditItem.isUnderIVA
    && creditItem.hasContractualMonthlyPayments
    && creditItem.consolidationType !== 'Full';
  const isShowRemainingTerm = NonRevolvingAccountTypes.includes(creditItem?.accountType)
    && (creditItem.isEquifax || isShowRemainingTermManual);
  const isShowManualPaymentHistory = creditItem.isEquifax === false
    && !!creditItem?.consolidationType
    && creditItem.hasContractualMonthlyPayments;

  return (
    <Collapsible
      id={`${credit.id}-credit-details`}
      title="Credit details"
      level={2}
      bodyCss={collapsibleWrapperCss}
    >

      {isShowForm && (
        <CreditDetailsContainer>
          {isShowLender && (
            <>
              <CreditDetailsRow>
                <ModelSelectSearchInput
                  field="supplier"
                  label="Lender"
                  options={lenders}
                  required
                />
                {creditItem?.supplier?.toString() === '0'
                  ? (
                    <ModelInput
                      field="otherSupplierName"
                      label="Lender name"
                      required
                    />
                  )
                  : <EmptyInput />}
              </CreditDetailsRow>
              {creditItem?.isEquifax === false && (
                <>
                  <RowBreakLine />
                  <CreditDetailsRow>
                    <RadioYesNoInput
                      field="hasContractualMonthlyPayments"
                      label="Does it have monthly payments?"
                      required
                    />
                  </CreditDetailsRow>
                </>
              )}

              {isShowRemainingTerm && (
                <>
                  <RowBreakLine />
                  <CreditDetailsRow>
                    <ModelNewNumericInput
                      field="overallRemainingTerm"
                      label="Overall remaining term"
                      suffix="months"
                      disabled={creditItem?.isEquifax}
                      required
                      integer
                      noGroupingSeperator
                    />
                    <EmptyInput />
                  </CreditDetailsRow>
                </>
              )}
            </>
          )}

          {isShowAffordability && (
            <>
              <DetailsTitle>AFFORDABILITY</DetailsTitle>

              <CreditDetailsRow>
                <ModelNewNumericInput
                  field="actualBalance"
                  label="Actual balance"
                  prefix="£"
                  isFixedDecimal
                  signed
                  required
                />
                <ModelNewNumericInput
                  field="actualMonthlyPayment"
                  label="Actual monthly payment"
                  prefix="£"
                  tooltip="This figure is from Equifax. Any amendments will require documentary evidence to support."
                  isFixedDecimal
                  required
                />
              </CreditDetailsRow>

              <CreditDetailsRow>
                <ModelNewNumericInput
                  field="resultingBalance"
                  label="Resulting balance"
                  prefix="£"
                  tooltip="This figure is the balance that will be remaining on the account once any consolidations have been factored in."
                  isFixedDecimal
                  required
                  disabled={
                    [
                      ConsolidationType.Full,
                      ConsolidationType.NoConsolidation,
                    ].includes(ConsolidationType[creditItem.consolidationType])
                  }
                />
                <ModelNewNumericInput
                  field="resultingMonthlyPayment"
                  label="Resulting monthly payment"
                  prefix="£"
                  isFixedDecimal
                  required
                  disabled={
                    [
                      ConsolidationType.Full,
                      ConsolidationType.NoConsolidation,
                    ].includes(ConsolidationType[creditItem.consolidationType])
                  }
                />
              </CreditDetailsRow>
            </>
          )}

          {isShowManualPaymentHistory && (
          <>
            <DetailsTitle>PAYMENT HISTORY</DetailsTitle>
            <CreditDetailsRow>
              <ModelNewNumericInput
                field="missedFullCMPLastTwelveMonths"
                label="How many missed full CMPs in the last 12 months?"
                noGroupingSeperator
                required
                integer
              />
              {isNonEmptyNumber(creditItem.missedFullCMPLastTwelveMonths)
                ? (
                  <ModelNewNumericInput
                    field="missedFullCMPLastSixMonths"
                    label="How many missed full CMPs in the last 6 months?"
                    disabled={
                      creditItem.missedFullCMPLastTwelveMonths === 12
                      || creditItem.missedFullCMPLastTwelveMonths === 0
                    }
                    noGroupingSeperator
                    required
                    integer
                  />
                )
                : <EmptyInput />}
            </CreditDetailsRow>

            {isNonEmptyNumber(creditItem.missedFullCMPLastTwelveMonths)
            && isNonEmptyNumber(creditItem.missedFullCMPLastSixMonths)
            && (
              <CreditDetailsRow>
                <ModelNewNumericInput
                  field="missedFullCMPLastThreeMonths"
                  label="How many missed full CMPs in the last 3 months?"
                  disabled={
                    creditItem.missedFullCMPLastSixMonths === 6
                    || creditItem.missedFullCMPLastSixMonths === 0
                  }
                  noGroupingSeperator
                  integer
                  required
                />
                <EmptyInput />
              </CreditDetailsRow>
            )}

            <CreditDetailsRow>
              <RadioYesNoInput
                field="inArrangement"
                label="In arrangement?"
                required
              />

              {creditItem.inArrangement && (
              <ModelNewNumericInput
                field="arrangementMonthlyPayment"
                label="What is the arranged payment?"
                prefix="£"
                isFixedDecimal
                required
              />
              )}
            </CreditDetailsRow>
          </>
          )}
        </CreditDetailsContainer>
      )}

      {creditItem.isEquifax && (!isShowForm || !creditItem.consolidationType) && (
        <EquifaxAccordionHeaderShadow />
      )}

      <EquifaxDetails creditItem={creditItem} creditType="unsecured" />
    </Collapsible>
  );
}
